// Client-side simulation of the backend `ServiceMissingFieldsCalculator` for
// the config editor's Preview tab: given a product's requirement groups and a
// sandbox form state, derive each field's effective criticality and the
// resulting information-availability status. Semantics follow
// `dev-context/conventions/condition-dsl.md` — in particular §parent-level
// LIST aggregation (one-operand `some`/`all` over the parent's own entries).
//
// This is a *simulation* for PMs to see rules react while typing; the
// authoritative evaluation stays on the backend.

import jsonLogic from 'json-logic-js';

import type {
    DataCriticality,
    FieldCondition,
    ProductRequirementGroup,
    RequirementField,
    RequirementFieldWithChildren,
} from '../../real-estate-asset-management/data-collection.dto';
// Side effect: importing the helpers registers the custom json-logic operators
// (exists / not_exists / count_*) shared with the customer form.
import {
    buildFieldTree,
    type FieldValueMap,
    isFieldVisible,
} from '../../real-estate-asset-management/components/data-collection.helpers';
import type { InformationAvailabilityStatus } from '../../real-estate-asset-management/rea-collection-view.dto';

const CRITICALITY_NAMES: ReadonlySet<string> = new Set(['mandatory', 'important', 'optional', 'irrelevant']);

/** Criticalities that count toward missing information (per the backend calculator). */
const BLOCKING: ReadonlySet<DataCriticality> = new Set(['mandatory', 'important'] as DataCriticality[]);

export interface FieldVerdict {
    code: string;
    label: string;
    /** Effective criticality after evaluating `criticalityWhen` (or the static value). */
    criticality: DataCriticality;
    filled: boolean;
    /** True when the field currently counts as missing information. */
    missing: boolean;
}

/** The sandbox always has a "connected product", so NO_SERVICE_CONNECTED can't occur. */
export type SimulatedStatus = Extract<
    InformationAvailabilityStatus,
    'MANDATORY_INFO_MISSING' | 'IMPORTANT_INFO_MISSING' | 'ALL_AVAILABLE'
>;

export interface AvailabilitySimulation {
    status: SimulatedStatus;
    verdicts: FieldVerdict[];
}

/**
 * Mirrors the backend's blank check: null/undefined, whitespace-only strings,
 * and empty arrays are "not filled"; `0` and `false` are legitimate answers.
 */
export function isValueFilled(value: unknown): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (Array.isArray(value)) return value.length > 0;
    return true;
}

function isRecord(node: unknown): node is Record<string, unknown> {
    return typeof node === 'object' && node !== null && !Array.isArray(node);
}

/**
 * Replace every ONE-operand `{"some": [x]}` / `{"all": [x]}` node with a
 * boolean literal computed from [entryValidities]. The one-operand form is a
 * backend extension (parent-level aggregation binds the LIST's own code to its
 * entries' validity booleans) that stock json-logic-js cannot evaluate — the
 * two-operand predicate form is left untouched (json-logic-js handles it).
 * Empty validities → `false`, matching the DSL ("a zero-row list makes
 * some/all false").
 */
function resolveOneOperandAggregations(node: unknown, entryValidities: boolean[]): unknown {
    if (Array.isArray(node)) return node.map((child) => resolveOneOperandAggregations(child, entryValidities));
    if (!isRecord(node)) return node;
    const keys = Object.keys(node);
    if (keys.length === 1 && (keys[0] === 'some' || keys[0] === 'all')) {
        const operand = node[keys[0]];
        if (Array.isArray(operand) && operand.length === 1) {
            return keys[0] === 'some'
                ? entryValidities.some(Boolean)
                : entryValidities.length > 0 && entryValidities.every(Boolean);
        }
    }
    const next: Record<string, unknown> = {};
    for (const key of keys) next[key] = resolveOneOperandAggregations(node[key], entryValidities);
    return next;
}

/** Whether a rule contains the one-operand parent-aggregation form at all. */
export function usesParentAggregation(node: unknown): boolean {
    if (node == null) return false;
    if (Array.isArray(node)) return node.some(usesParentAggregation);
    if (!isRecord(node)) return false;
    const keys = Object.keys(node);
    if (keys.length === 1 && (keys[0] === 'some' || keys[0] === 'all')) {
        const operand = node[keys[0]];
        if (Array.isArray(operand) && operand.length === 1) return true;
    }
    return keys.some((key) => usesParentAggregation(node[key]));
}

/**
 * Evaluate a field's effective criticality: `criticalityWhen` against the form
 * state (with one-operand aggregations pre-resolved from [entryValidities]),
 * falling back to the static `dataCriticality` when the rule is absent,
 * throws, or doesn't yield a criticality name — mirroring the backend's
 * fail-safe fallback.
 */
export function effectiveCriticality(
    field: Pick<RequirementField, 'dataCriticality' | 'criticalityWhen'>,
    values: FieldValueMap,
    entryValidities: boolean[] = [],
): DataCriticality {
    if (field.criticalityWhen == null) return field.dataCriticality;
    try {
        const rule = resolveOneOperandAggregations(field.criticalityWhen, entryValidities) as FieldCondition;
        const raw: unknown = jsonLogic.apply(rule, values);
        if (typeof raw === 'string' && CRITICALITY_NAMES.has(raw.toLowerCase())) {
            return raw.toLowerCase() as DataCriticality;
        }
        return field.dataCriticality;
    } catch {
        return field.dataCriticality;
    }
}

type EntryLike = Record<string, unknown>;

function asEntries(value: unknown): EntryLike[] {
    return Array.isArray(value) ? value.filter(isRecord) : [];
}

/**
 * Per-entry validity roll-up (DSL §parent-level aggregation): an entry is
 * valid when every visible sub-field whose effective criticality (by its own
 * per-row rule, with row values shadowing top-level values) is blocking has
 * been filled.
 */
export function entryValidities(
    list: RequirementFieldWithChildren,
    entries: EntryLike[],
    parentValues: FieldValueMap,
): boolean[] {
    return entries.map((entry) => {
        const scope: FieldValueMap = { ...parentValues, ...entry };
        return list.subFields
            .filter((sub) => isFieldVisible(sub, scope))
            .every((sub) => !BLOCKING.has(effectiveCriticality(sub, scope)) || isValueFilled(scope[sub.code]));
    });
}

function pushVerdict(verdicts: FieldVerdict[], field: RequirementField, criticality: DataCriticality, filled: boolean) {
    verdicts.push({
        code: field.code,
        label: field.label,
        criticality,
        filled,
        missing: BLOCKING.has(criticality) && !filled,
    });
}

/**
 * Walk one product's groups against the sandbox values and derive the status
 * ladder the backend applies: any mandatory missing → MANDATORY_INFO_MISSING,
 * else any important missing → IMPORTANT_INFO_MISSING, else ALL_AVAILABLE.
 */
export function simulateAvailability(groups: ProductRequirementGroup[], values: FieldValueMap): AvailabilitySimulation {
    const verdicts: FieldVerdict[] = [];

    for (const group of groups) {
        for (const root of buildFieldTree(group.requirementFields)) {
            if (!isFieldVisible(root, values)) continue;

            if (root.fieldType === 'list') {
                const entries = asEntries(values[root.code]);
                const validities = entryValidities(root, entries, values);

                if (usesParentAggregation(root.criticalityWhen)) {
                    // Aggregated list: surface ONLY the parent, at its rolled-up
                    // criticality — "filled" once the aggregation relaxes it to
                    // a non-blocking level (DSL: sub-field codes are not surfaced).
                    const criticality = effectiveCriticality(root, values, validities);
                    pushVerdict(verdicts, root, criticality, !BLOCKING.has(criticality));
                    continue;
                }

                // Per-entry list: each row's blocking sub-fields must be filled;
                // an empty list surfaces the parent itself at its own criticality.
                const parentCriticality = effectiveCriticality(root, values);
                if (entries.length === 0) {
                    pushVerdict(verdicts, root, parentCriticality, false);
                    continue;
                }
                pushVerdict(verdicts, root, parentCriticality, true);
                entries.forEach((entry, index) => {
                    const scope: FieldValueMap = { ...values, ...entry };
                    for (const sub of root.subFields) {
                        if (!isFieldVisible(sub, scope)) continue;
                        const criticality = effectiveCriticality(sub, scope);
                        if (!BLOCKING.has(criticality)) continue;
                        const filled = isValueFilled(scope[sub.code]);
                        if (!filled) {
                            verdicts.push({
                                code: `${sub.code} #${index + 1}`,
                                label: `${sub.label} (${root.name} ${index + 1})`,
                                criticality,
                                filled,
                                missing: true,
                            });
                        }
                    }
                });
                continue;
            }

            const criticality = effectiveCriticality(root, values);
            pushVerdict(verdicts, root, criticality, isValueFilled(values[root.code]));
        }
    }

    const status: InformationAvailabilityStatus = verdicts.some((v) => v.missing && v.criticality === 'mandatory')
        ? 'MANDATORY_INFO_MISSING'
        : verdicts.some((v) => v.missing)
          ? 'IMPORTANT_INFO_MISSING'
          : 'ALL_AVAILABLE';

    return { status, verdicts };
}
